<template>
  <div>
    <div class="tile">
      <div class="form-inline">
        <date-picker v-model="filterFormModel.period" type="month" value-type="YYYY-MM" format="MM/YYYY"
          :lang="$i18n.locale" :clearable="false" :placeholder="$t('selectMonth')" class="mr-1"
          @change="load"></date-picker>
        <button @click="load" class="btn btn-primary mr-1">{{ $t('search') }}</button>
        <button @click="reset" class="btn btn-secondary mr-1">Reset</button>
        <button @click="exportExcel" class="btn btn-primary mr-1" :disabled="!rows.length">
          <i class="fa fa-download"></i> {{ $t('exportExcel') }}
        </button>
      </div>
    </div>

    <div class="tile">
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center">
          <caption class="text-center text-uppercase font-weight-bold" style="caption-side: top; color: #000;">
            {{ $t('reportTitle') }} {{ monthLabel }}
          </caption>
          <thead>
            <tr>
              <th>{{ $t('stt') }}</th>
              <th>{{ $t('fullName') }}</th>
              <th>{{ $t('position') }}</th>
              <th>{{ $t('kpiForCommission') }}</th>
              <th>{{ $t('callFeeUnder12', [monthLabel]) }}</th>
              <th>{{ $t('commissionPercent') }}</th>
              <th>{{ $t('callFeeOver12Big') }}</th>
              <th>{{ $t('commissionPercent') }}</th>
              <th class="text-success">{{ $t('commissionReceived') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in rows" :key="item.userId">
              <td>{{ index + 1 }}</td>
              <td>{{ item.name }}</td>
              <td>{{ position(item.role) }}</td>
              <td :style="kpiRateStyle(item.kpiPercent)">{{ item.kpiBracket }}</td>
              <td class="text-right">{{ item.feeUnder12 | numFormat }}</td>
              <td>{{ percent(item.pctUnder12) }}</td>
              <td class="text-right">{{ item.feeOver12Big | numFormat }}</td>
              <td>{{ percent(item.pctOver12Big) }}</td>
              <td class="text-right font-weight-bold text-success">
                <span v-if="item.commission > 0">{{ item.commission | numFormat }}</span>
                <span v-else>-</span>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="9" class="text-danger">{{ $t('noData') }}</td>
            </tr>
            <tr v-if="rows.length" class="font-weight-bold" style="background-color: #fcf8e3;">
              <td colspan="4" class="text-left">{{ $t('grandTotal') }}</td>
              <td class="text-right">{{ total.feeUnder12 | numFormat }}</td>
              <td></td>
              <td class="text-right">{{ total.feeOver12Big | numFormat }}</td>
              <td></td>
              <td class="text-right text-success">{{ total.commission | numFormat }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="tile">
      <div class="form-inline mb-2">
        <b-form-input v-model="filterFormModel.q" :placeholder="$t('keywordPlaceholder')" class="mr-1 mb-1"
          @keyup.native.enter="load"></b-form-input>
        <client-select :model="filterFormModel" attribute="client" :isFilter="true" place-holder="selectClient"
          class="mr-1 mb-1"></client-select>
        <admin-user-select v-if="$store.state.user.role !== 'sales'" :model="filterFormModel" attribute="sales"
          role-filter="sales" :place-holder="$t('selectSales')" class="mr-1 mb-1"></admin-user-select>
        <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
        <button @click="resetDetail" class="btn btn-secondary mr-1 mb-1">Reset</button>
      </div>
      <label class="text-uppercase font-weight-bold mb-0 mr-3" style="color: #000;">
        {{ $t('detailTitle') }} {{ monthLabel }}
      </label>
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center call-fee-detail-table">
          <thead>
            <tr>
              <th class="align-middle">{{ $t('stt') }}</th>
              <th class="align-middle">{{ $t('dSales') }}</th>
              <th class="align-middle">{{ $t('dClientName') }}</th>
              <th class="align-middle">{{ $t('dDomain') }}</th>
              <th class="align-middle">{{ $t('dActiveTime') }}</th>
              <th class="align-middle">{{ $t('dReExtendTime') }}</th>
              <th class="align-middle">{{ $t('dMonthsSinceActive') }}</th>
              <th class="align-middle">{{ $t('dMonthsSinceReExtend') }}</th>
              <th class="align-middle">{{ $t('dUserCount') }}</th>
              <th class="align-middle">{{ $t('dCallFee') }}</th>
              <th class="align-middle">{{ $t('dKpiBracket') }}</th>
              <th class="align-middle">{{ $t('dCommissionPercent') }}</th>
              <th class="align-middle">{{ $t('dCommission') }}</th>
              <th class="align-middle">{{ $t('dNote') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in details" :key="index" :style="row.isBig ? 'background-color: #f2dede;' : ''">
              <td class="align-middle">{{ index + 1 }}</td>
              <td class="text-left align-middle">{{ row.sales }}</td>
              <td class="text-left align-middle">{{ row.companyName }}</td>
              <td class="text-left align-middle">{{ row.domain }}</td>
              <td class="align-middle">{{ fmtDate(row.activeTime) }}</td>
              <td class="align-middle">{{ fmtDate(row.extendAgainTime) }}</td>
              <td class="text-right align-middle">{{ row.monthsSinceActive }}</td>
              <td class="text-right align-middle">{{ row.extendAgainTime > 0 ? row.monthsSinceReExtend : '-' }}</td>
              <td class="text-right align-middle">{{ row.userLimit | numFormat }}</td>
              <td class="text-right align-middle">{{ row.fee | numFormat }}</td>
              <td class="align-middle" :style="kpiRateStyle(row.kpiPercent)">{{ row.kpiBracket }}</td>
              <td class="text-right align-middle">{{ percent(row.commissionPercent) }}</td>
              <td class="text-right font-weight-bold align-middle">
                <span v-if="row.commission > 0">{{ row.commission | numFormat }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-left align-middle">{{ row.note }}</td>
            </tr>
            <tr v-if="!details.length">
              <td colspan="14" class="text-danger">{{ $t('noDetail') }}</td>
            </tr>
            <tr v-if="details.length" class="font-weight-bold" style="background-color: #fcf8e3;">
              <td colspan="9" class="text-left align-middle">{{ $t('grandTotal') }}</td>
              <td class="text-right align-middle">{{ detailTotal.fee | numFormat }}</td>
              <td></td>
              <td></td>
              <td class="text-right text-danger align-middle">{{ detailTotal.commission | numFormat }}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Excel from 'exceljs';
import { saveAs } from 'file-saver';
import AdminUserSelect from '@/components/AdminUserSelect';
import ClientSelect from '@/components/ClientSelect';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/vi';

export default {
  components: { AdminUserSelect, ClientSelect, DatePicker },
  data() {
    return {
      items: [],
      details: [],
      filterFormModel: { period: moment().format('YYYY-MM'), sales: null, q: '', client: null },
    }
  },
  computed: {
    monthLabel() {
      return moment(this.filterFormModel.period, 'YYYY-MM').format('MM/YYYY');
    },
    rows() {
      // Sắp xếp: khu vực (VP Miền Bắc trước), trong khu vực theo hoa hồng giảm dần
      return [...this.items].sort((a, b) => {
        let office = (a.office || '').localeCompare(b.office || '');
        if (office !== 0) {
          return office;
        }
        return b.commission - a.commission;
      });
    },
    total() {
      return this.items.reduce((acc, item) => {
        acc.feeUnder12 += item.feeUnder12 || 0;
        acc.feeOver12Big += item.feeOver12Big || 0;
        acc.commission += item.commission || 0;
        return acc;
      }, { feeUnder12: 0, feeOver12Big: 0, commission: 0 });
    },
    detailTotal() {
      return this.details.reduce((acc, row) => {
        acc.fee += row.fee || 0;
        acc.commission += row.commission || 0;
        return acc;
      }, { fee: 0, commission: 0 });
    }
  },
  methods: {
    load() {
      let f = this.filterFormModel;
      let [year, month] = f.period.split('-');
      // Bảng tổng hợp hiển thị toàn bộ NVKD (không lọc theo sales)
      let params = { month: parseInt(month), year: parseInt(year) };
      // Bảng chi tiết có bộ lọc riêng (NVKD phụ trách/từ khoá/khách hàng)
      let detailParams = Object.assign({}, params);
      if (f.sales) detailParams.sales = f.sales;
      if (f.q) detailParams.q = f.q;
      if (f.client) detailParams.client = f.client;
      let loader = this.$loading.show();
      Promise.all([
        this.$service.get('/report/call-fee-commission', { params }),
        this.$service.get('/report/call-fee-commission-detail', { params: detailParams }),
      ]).then(([summary, detail]) => {
        this.items = summary.data;
        this.details = detail.data;
      }).finally(() => {
        loader.hide();
      });
    },
    reset() {
      this.filterFormModel.period = moment().format('YYYY-MM');
      this.load();
    },
    // Reset bộ lọc riêng của bảng chi tiết
    resetDetail() {
      this.filterFormModel.sales = null;
      this.filterFormModel.q = '';
      this.filterFormModel.client = null;
      this.load();
    },
    fmtDate(t) {
      return t ? moment(t).format('DD/MM/YYYY') : '-';
    },
    percent(value) {
      return (value || 0).toFixed(2) + '%';
    },
    kpiRateStyle(percent) {
      if (percent === null || percent === undefined) {
        return {};
      }
      let color = '';
      if (percent < 60) {
        color = '#dc3545';
      } else if (percent < 100) {
        color = '#e0a800';
      } else if (percent <= 120) {
        color = '#28a745';
      } else if (percent <= 150) {
        color = '#007bff';
      } else if (percent > 150) {
        color = '#6f42c1';
      }
      return color ? { color, fontWeight: 'bold' } : {};
    },
    position(role) {
      const map = {
        supervisor: this.$t('roleSupervisor'),
        manager: this.$t('roleManager'),
      };
      return map[role] || this.$t('roleConsultant');
    },
    xlsxFillRow(row, argb, count) {
      for (let c = 1; c <= count; c++) {
        row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
      }
    },
    xlsxBorderRange(sheet, r1, r2, c1, c2) {
      let thin = { style: 'thin', color: { argb: 'FFBFBFBF' } };
      for (let r = r1; r <= r2; r++) {
        let row = sheet.getRow(r);
        for (let c = c1; c <= c2; c++) {
          let cell = row.getCell(c);
          cell.border = { top: thin, left: thin, bottom: thin, right: thin };
          cell.alignment = Object.assign({ vertical: 'middle' }, cell.alignment || {});
        }
      }
    },
    async exportExcel() {
      let money = '#,##0';
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet(this.$t('sheetCallFee'));
      sheet.columns = [
        { header: this.$t('stt'), key: 'stt', width: 6 },
        { header: this.$t('fullName'), key: 'name', width: 22 },
        { header: this.$t('position'), key: 'position', width: 16 },
        { header: this.$t('kpiForCommission'), key: 'kpiBracket', width: 16 },
        { header: this.$t('callFeeUnder12', [this.monthLabel]), key: 'feeUnder12', width: 22, style: { numFmt: money } },
        { header: this.$t('commissionPercent'), key: 'pctUnder12', width: 12 },
        { header: this.$t('callFeeOver12Big'), key: 'feeOver12Big', width: 24, style: { numFmt: money } },
        { header: this.$t('commissionPercent') + ' ', key: 'pctOver12Big', width: 12 },
        { header: this.$t('commissionReceived'), key: 'commission', width: 18, style: { numFmt: money } },
      ];
      this.rows.forEach((item, index) => {
        sheet.addRow({
          stt: index + 1,
          name: item.name,
          position: this.position(item.role),
          kpiBracket: item.kpiBracket,
          feeUnder12: item.feeUnder12 || 0,
          pctUnder12: this.percent(item.pctUnder12),
          feeOver12Big: item.feeOver12Big || 0,
          pctOver12Big: this.percent(item.pctOver12Big),
          commission: item.commission || 0,
        });
      });
      if (this.rows.length) {
        let totalRow = sheet.addRow({
          name: this.$t('grandTotal'),
          feeUnder12: this.total.feeUnder12,
          feeOver12Big: this.total.feeOver12Big,
          commission: this.total.commission,
        });
        totalRow.font = { bold: true, color: { argb: 'FFC00000' } };
        this.xlsxFillRow(totalRow, 'FFFCF8E3', 9);
      }
      sheet.getRow(1).font = { bold: true };
      sheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.xlsxFillRow(sheet.getRow(1), 'FFF2F2F2', 9);
      sheet.views = [{ state: 'frozen', ySplit: 1 }];
      this.xlsxBorderRange(sheet, 1, sheet.rowCount, 1, 9);

      // Sheet 2: Chi tiết hoa hồng cước gọi theo từng khách hàng
      let detail = workbook.addWorksheet(this.$t('sheetDetail'));
      detail.columns = [
        { header: this.$t('stt'), key: 'stt', width: 6 },
        { header: this.$t('dSales'), key: 'sales', width: 20 },
        { header: this.$t('dClientName'), key: 'companyName', width: 26 },
        { header: this.$t('dDomain'), key: 'domain', width: 22 },
        { header: this.$t('dActiveTime'), key: 'activeTime', width: 16 },
        { header: this.$t('dReExtendTime'), key: 'extendAgainTime', width: 16 },
        { header: this.$t('dMonthsSinceActive'), key: 'monthsSinceActive', width: 14 },
        { header: this.$t('dMonthsSinceReExtend'), key: 'monthsSinceReExtend', width: 14 },
        { header: this.$t('dUserCount'), key: 'userLimit', width: 10 },
        { header: this.$t('dCallFee'), key: 'fee', width: 18, style: { numFmt: money } },
        { header: this.$t('dKpiBracket'), key: 'kpiBracket', width: 14 },
        { header: this.$t('dCommissionPercent'), key: 'commissionPercent', width: 12 },
        { header: this.$t('dCommission'), key: 'commission', width: 18, style: { numFmt: money } },
        { header: this.$t('dNote'), key: 'note', width: 32 },
      ];
      this.details.forEach((row, index) => {
        let r = detail.addRow({
          stt: index + 1,
          sales: row.sales,
          companyName: row.companyName,
          domain: row.domain,
          activeTime: this.fmtDate(row.activeTime),
          extendAgainTime: this.fmtDate(row.extendAgainTime),
          monthsSinceActive: row.monthsSinceActive,
          monthsSinceReExtend: row.extendAgainTime > 0 ? row.monthsSinceReExtend : '-',
          userLimit: row.userLimit || 0,
          fee: row.fee || 0,
          kpiBracket: row.kpiBracket,
          commissionPercent: this.percent(row.commissionPercent),
          commission: row.commission || 0,
          note: row.note,
        });
        if (row.isBig) {
          this.xlsxFillRow(r, 'FFF2DEDE', 14);
        }
      });
      if (this.details.length) {
        let totalRow = detail.addRow({
          companyName: this.$t('grandTotal'),
          fee: this.detailTotal.fee,
          commission: this.detailTotal.commission,
        });
        totalRow.font = { bold: true, color: { argb: 'FFC00000' } };
        this.xlsxFillRow(totalRow, 'FFFCF8E3', 14);
      }
      detail.getRow(1).font = { bold: true };
      detail.getRow(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.xlsxFillRow(detail.getRow(1), 'FFF2F2F2', 14);
      detail.views = [{ state: 'frozen', ySplit: 1 }];
      this.xlsxBorderRange(detail, 1, detail.rowCount, 1, 14);

      let buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `hoa-hong-cuoc-goi-${this.filterFormModel.period}.xlsx`);
    }
  },
  mounted() {
    this.load();
  },
  i18n: {
    messages: {
      en: {
        reportTitle: 'Call fee month',
        selectSales: 'Select sales person',
        selectMonth: 'Select month',
        stt: 'No.',
        fullName: 'Full name',
        position: 'Position',
        kpiForCommission: 'KPI for commission',
        callFeeUnder12: 'Call fee {0} (customers under 12 months)',
        callFeeOver12Big: 'Call fee customers >15 users, >12 months',
        commissionPercent: '% Commission',
        commissionReceived: 'Commission received',
        roleConsultant: 'Consultant',
        roleSupervisor: 'Supervisor',
        roleManager: 'Manager',
        grandTotal: 'Grand total',
        exportExcel: 'Export Excel',
        sheetCallFee: 'Call fee commission',
        sheetDetail: 'Call fee commission detail',
        noData: 'No KPI data for this month',
        keywordPlaceholder: 'Keyword...',
        detailTitle: 'Call fee commission detail month',
        dSales: 'Sales in charge',
        dClientName: 'Company name',
        dDomain: 'Domain',
        dActiveTime: 'Activation time',
        dReExtendTime: 'Re-sign time',
        dMonthsSinceActive: 'Months since activation',
        dMonthsSinceReExtend: 'Months since re-sign',
        dUserCount: 'Users',
        dCallFee: 'Commissionable call fee',
        dKpiBracket: 'KPI for commission',
        dCommissionPercent: '% Commission',
        dCommission: 'Commission received',
        dNote: 'Note',
        noDetail: 'No call fee commission for this month',
      },
      vi: {
        reportTitle: 'Cước gọi tháng',
        selectSales: 'Chọn nhân viên kinh doanh',
        selectMonth: 'Chọn tháng',
        stt: 'STT',
        fullName: 'Họ tên',
        position: 'Chức vụ',
        kpiForCommission: 'KPI tính hoa hồng',
        callFeeUnder12: 'Cước gọi tháng {0} (khách hàng dưới 12 tháng)',
        callFeeOver12Big: 'Cước gọi khách hàng >15 user, >12 tháng',
        commissionPercent: '% Hoa hồng nhận',
        commissionReceived: 'Hoa hồng nhận được',
        roleConsultant: 'Nhân viên tư vấn',
        roleSupervisor: 'Trưởng nhóm',
        roleManager: 'Quản lý',
        grandTotal: 'Tổng cộng',
        exportExcel: 'Xuất dữ liệu',
        sheetCallFee: 'Hoa hồng cước gọi',
        sheetDetail: 'Chi tiết hoa hồng cước gọi',
        noData: 'Chưa có dữ liệu KPI cho tháng này',
        keywordPlaceholder: 'Từ khoá...',
        detailTitle: 'Chi tiết hoa hồng cước gọi tháng',
        dSales: 'Kinh doanh phụ trách',
        dClientName: 'Tên công ty',
        dDomain: 'Domain',
        dActiveTime: 'Thời gian kích hoạt',
        dReExtendTime: 'Thời gian tái ký',
        dMonthsSinceActive: 'Số tháng kể từ khi kích hoạt',
        dMonthsSinceReExtend: 'Số tháng kể từ khi tái ký',
        dUserCount: 'Số user',
        dCallFee: 'Cước gọi tính hoa hồng',
        dKpiBracket: 'KPI tính hoa hồng',
        dCommissionPercent: '% Hoa hồng',
        dCommission: 'Hoa hồng được nhận',
        dNote: 'Ghi chú',
        noDetail: 'Chưa có hoa hồng cước gọi cho tháng này',
      }
    }
  }
}
</script>

<style scoped>
.c-template-table table.table thead th {
  background-color: #f2f2f2 !important;
}

.call-fee-detail-table th,
.call-fee-detail-table td {
  white-space: nowrap;
}
</style>
